import React, { useState, useEffect } from "react";
import { Menu, X, Phone, MapPin } from "lucide-react";
import { navLinks, siteInfo } from "../mock/mock";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const LOCATION_LINKS = {
  Udupi:
    "https://www.google.com/search?kgmid=%2Fg%2F11hz8lbd6s&hl=en-IN&q=RR+Bros+Bike+Rental+Udupi",
  Manipal:
    "https://www.google.com/search?kgmid=%2Fg%2F11h4znl120&hl=en-IN&q=RR+Bros+Bike+Rental+Manipal",
};

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const telHref = `tel:${siteInfo.phone.replace(/\s/g, "")}`;

  return (
    <header className="sticky top-0 z-50 w-full">
      {/* Top bar */}
      <div className="bg-neutral-900 text-white text-xs sm:text-sm py-2 px-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-4 text-center">
          <a
            href={telHref}
            className="flex items-center gap-2 hover:text-orange-400 transition-colors"
          >
            <Phone className="w-3.5 h-3.5" />
            <span>Call us: {siteInfo.phone}</span>
          </a>
          <span className="hidden sm:block text-neutral-500">|</span>
          <span className="text-neutral-300">{siteInfo.timings.udupi}</span>
          <span className="hidden sm:block text-neutral-500">|</span>
          <span className="text-neutral-300">{siteInfo.timings.manipal}</span>
        </div>
      </div>

      {/* Main nav */}
      <nav
        className={`bg-white transition-shadow duration-300 ${
          scrolled ? "shadow-md" : "shadow-sm"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20 lg:h-24">
            {/* Logo */}
            <a
              href="/"
              className="flex items-center gap-2 sm:gap-3 group"
              aria-label="RR Bros Bike Rentals - Home"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              <img
                src="/logo.jpeg?v=2"
                alt="RR Bros Bike Rental - Bike & Scooty Rental in Udupi and Manipal"
                className="h-10 sm:h-14 lg:h-16 w-auto rounded-md shadow-sm group-hover:shadow-md transition-shadow"
              />
              <div className="flex flex-col leading-none">
                <span className="font-extrabold text-neutral-900 text-sm sm:text-xl lg:text-2xl tracking-tight">
                  RR Bros
                </span>
                <span className="text-[8px] sm:text-[11px] lg:text-[12px] font-semibold text-orange-600 tracking-[0.14em] sm:tracking-[0.18em] mt-1 uppercase whitespace-nowrap">
                  Bike Rental
                </span>
              </div>
            </a>

            {/* Desktop nav */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="text-neutral-800 font-semibold text-sm uppercase tracking-wide hover:text-orange-600 transition-colors relative after:content-[''] after:absolute after:left-0 after:-bottom-1 after:w-0 after:h-0.5 after:bg-orange-500 hover:after:w-full after:transition-all"
                >
                  {link.name}
                </a>
              ))}
            </div>

            {/* Location dropdown + mobile menu */}
            <div className="flex items-center gap-1.5 sm:gap-3">
              <DropdownMenu>
                <DropdownMenuTrigger className="inline-flex items-center gap-1 sm:gap-1.5 px-3 sm:px-4 h-10 sm:h-11 rounded-full border-2 border-neutral-900 hover:bg-neutral-900 hover:text-white text-neutral-900 text-[11px] sm:text-sm font-bold uppercase tracking-wider transition-colors focus:outline-none whitespace-nowrap">
                  <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span>Get Location</span>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  {Object.entries(LOCATION_LINKS).map(([name, url]) => (
                    <DropdownMenuItem
                      key={name}
                      onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
                      className="cursor-pointer font-semibold"
                    >
                      <MapPin className="w-4 h-4 mr-2 text-orange-500" />
                      {name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <a
                href={telHref}
                className="hidden sm:inline-flex items-center gap-2 px-4 lg:px-5 h-11 rounded-full bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold uppercase tracking-wider shadow-md hover:shadow-orange-300/50 transition-all"
                data-testid="header-call-cta"
              >
                <Phone className="w-4 h-4" />
                Call Us
              </a>

              <button
                className="lg:hidden p-1.5 sm:p-2 -mr-1 sm:-mr-2 text-neutral-800 hover:text-orange-600 transition-colors"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label="Toggle menu"
                data-testid="header-mobile-menu-toggle"
              >
                {isMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile menu */}
          {isMenuOpen && (
            <div className="lg:hidden pb-4 border-t border-neutral-100 pt-4 space-y-1">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={() => setIsMenuOpen(false)}
                  className="block px-3 py-2.5 text-neutral-800 font-semibold text-sm uppercase tracking-wide hover:bg-orange-50 hover:text-orange-600 rounded-md transition-colors"
                >
                  {link.name}
                </a>
              ))}
            </div>
          )}
        </div>
      </nav>
    </header>
  );
};

export default Header;
